import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  sendVerificationCode,
  sendVerificationCodeFailed,
  verifyCode,
  verifyCodeReset,
} from '../../../actions';
import { useOtpSMSFlow } from '../../../hooks';
import { getMaskedPhoneNumber, isEmailValid } from '../../../services/utils';
import { OtpInputContainer } from './OtpInputContainer';

import styles from './css/OtpFlow.module.css';

const OtpFlow = () => {
  const store = useSelector(({ personalInformation }) => ({
    personalInformation,
  }), shallowEqual);
  const { personalInformation: { userInfo } } = store;
  const dispatch = useDispatch();
  const useOtpBySms = useOtpSMSFlow();

  const [otpCode, setOtpCode] = useState('');
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const verificationState = useSelector(
    ({ otpVerification }) => otpVerification.verificationCode,
    shallowEqual,
  );

  const { t } = useTranslation('common');

  const phoneNumber = useMemo(() => userInfo?.PhoneNumber
    || '', [userInfo]);

  const email = useMemo(() => userInfo?.LoginEmail
    || '', [userInfo]);

  const sendOtpCode = useCallback(async () => {
    if (!useOtpBySms) {
      if (!email || !isEmailValid(email)) {
        dispatch(sendVerificationCodeFailed(t('otpFlow.invalidEmail')));
        return;
      }
    }

    dispatch(sendVerificationCode({ email, phoneNumber }));
    setResendTimer(15);

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [
    dispatch,
    phoneNumber,
    t,
  ]);

  const validateOtpCode = useCallback(async (code) => {
    dispatch(verifyCode({ code, email, phoneNumber }));
  }, [dispatch, email, phoneNumber]);

  // Watch for verification state changes
  useEffect(() => {
    if (verificationState.verified) {
      setOtpSuccess(true);
    } else if (verificationState.error) {
      setOtpCode('');
    }
  }, [
    verificationState.verified,
    verificationState.error,
    verificationState.success,
  ]);

  const resendOtpCode = useCallback(() => {
    if (resendTimer > 0) {
      return;
    }

    sendOtpCode();
  }, [resendTimer, sendOtpCode]);

  const handleBackClick = useCallback(() => {
    dispatch(verifyCodeReset());
    setOtpSuccess(false);
  }, [dispatch]);

  return (
    <div className={styles.OtpFlowContainer}>
      <p className={styles.OtpFlowDescription}>
        {t('otpFlow.description', {
          value: useOtpBySms ? getMaskedPhoneNumber(phoneNumber) : email,
        })}

        <br />

        {t('otpFlow.enterPasscode')}
      </p>

      <OtpInputContainer
        length={6}
        onComplete={validateOtpCode}
        otpCode={otpCode}
        otpError={verificationState.error}
        patternPerBox="\d"
        setOtpCode={setOtpCode}
      />

      {verificationState.error && (
        <div className={styles.OtpError}>
          {verificationState.error}
        </div>
      )}

      {otpSuccess && (
        <div className={styles.OtpSuccess}>
          {t('otpFlow.verificationSuccess')}
        </div>
      )}

      <div className={styles.OtpResendContainer}>
        {resendTimer > 0 ? (
          <span className={styles.OtpResendText}>
            {t('otpFlow.resendTimer', { seconds: resendTimer })}
          </span>
        ) : (
          <button
            className={styles.OtpResendButton}
            onClick={resendOtpCode}
            type="button"
          >
            {t('otpFlow.resendButton')}
          </button>
        )}
      </div>

      <button
        className={styles.OtpBackButton}
        disabled={verificationState.isLoading}
        onClick={handleBackClick}
        type="button"
      >
        <span>{t('otpFlow.back')}</span>
      </button>
    </div>
  );
};

export { OtpFlow };
